import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { ADMIN_ROLES } from "../../utils/constants";
import { capitalize, debounce } from "lodash";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Input,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogActions,
  DialogTitle,
  FormGroup,
  FormControlLabel,
  Switch,
  Grid,
  Typography,
  Chip,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Stack,
  Paper,
  Container,
  Fade,
  Zoom,
  FormControl,
  Select,
  MenuItem
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PersonIcon from "@mui/icons-material/Person";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import { userServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader } from "../../components/CustomLoader";
import { fDate } from "../../utils/formatTime";
import { formatPrice } from "../../utils/formatNumber";
import { TrimText } from "../../utils/helpers";
import { Helmet } from "react-helmet-async";
import { Diamond, Token } from "@mui/icons-material";

export default function WalletManagementList() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [totalWallets, setTotalWallets] = useState(0);
  const [totalBalance, setTotalBalance] = useState(0);
  const [averageBalance, setAverageBalance] = useState(0);

  const fetchUsers = async (query) => {
    const body = {
      searchItem: query || "",
      pageNumber: currentPage,
      pageSize: pageSize,
      status: status,
    };
    try {
      setIsLoading(true);
      setLoaderDelay(false);
      const res = await userServices.getUser(body);
      console.log('API Response:', res?.data?.data);
      setTimeout(() => {
        if (res.status === 200) {
          setData(res?.data?.data?.result);
          setTotalPages(res?.data?.data?.totalRecords);
          
          // Sync currentPage with API response if needed
          const apiPageNumber = res?.data?.data?.pageNumber;
          if (apiPageNumber && apiPageNumber !== currentPage) {
            setCurrentPage(apiPageNumber);
          }
          
          // Calculate statistics
          if (res?.data?.data?.result) {
            const wallets = res.data.data.result;
            setTotalWallets(wallets.length);
            
            const totalBal = wallets.reduce((sum, wallet) => sum + (wallet.balance || 0), 0);
            setTotalBalance(totalBal);
            setAverageBalance(wallets.length > 0 ? totalBal / wallets.length : 0);
          }
        }
        setLoaderDelay(true);
      }, 500);
    } catch (err) {
      setData(err?.response?.data?.message?.data);
      errorHandler(err);
      setLoaderDelay(false);
    } finally {
      setIsLoading(false);
      setLoaderDelay(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => fetchUsers(query), 400),
    [currentPage, pageSize, status]
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    setCurrentPage(1);
    debouncedSearch(query);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Update URL parameter to maintain page state
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', page);
    setSearchParams(newSearchParams);
  };

  // Initialize currentPage and viewMode from URL parameters
  useEffect(() => {
    const pageFromUrl = searchParams.get('page');
    const viewModeFromUrl = searchParams.get('view');
    
    if (pageFromUrl && !isNaN(pageFromUrl) && parseInt(pageFromUrl) > 0) {
      setCurrentPage(parseInt(pageFromUrl));
    }
    
    if (viewModeFromUrl && (viewModeFromUrl === 'grid' || viewModeFromUrl === 'list')) {
      setViewMode(viewModeFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchUsers(searchItem);
  }, [currentPage, pageSize, status, refresh]);

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "100px"
    },
    {
      name: "Full Name",
      sortable: true,
      selector: (row) => capitalize(TrimText(row.firstname + " " + row.lastname, 32)) ?? "-",
    },
    {
      name: "User Name",
      sortable: true,
      selector: (row) => row.username,
    },
    {
      name: "Email",
      sortable: true,
      selector: (row) => row.email,
    },
    {
      center: true,
      name: "Wallet Balance",
      sortable: true,
      minWidth: "220px",
      cell: (row) => (
        <div style={{ display: "flex", gap: "5px", flexWrap: "wrap", justifyContent: "center" }}>
          <Chip
            label={`£${formatPrice(row.balance || 0)}`}
            size="small"
            sx={{
              backgroundColor: 'rgba(76, 175, 80, 0.2)',
              color: '#4caf50',
              fontWeight: 600,
              border: '1px solid',
              borderColor: 'rgba(76, 175, 80, 0.3)'
            }}
          />
          <Chip
            icon={<Diamond style={{ fontSize: '14px' }} />}
            label={`${formatPrice(row.gemBalance || 0)}`}
            size="small"
            sx={{
              backgroundColor: "rgba(76, 97, 175, 0.2)",
              color: "#4c5bafff",
              fontWeight: 600,
              border: "1px solid",
              borderColor: "rgba(81, 76, 175, 0.3)"
            }}
          />
          <Chip
            icon={<Token style={{ fontSize: '14px', color: '#ffc107' }} />}
            label={`${formatPrice(row.sdCoinBalance || 0)}`}
            size="small"
            sx={{
              backgroundColor: "rgba(255, 193, 7, 0.2)",
              color: "#ffc107",
              fontWeight: 600,
              border: "1px solid",
              borderColor: "rgba(255, 193, 7, 0.3)",
              '& .MuiChip-icon': {
                color: '#ffc107'
              }
            }}
          />
        </div>
      ),
    },
    {
      name: "Phone",
      sortable: true,
      selector: (row) => row.phoneNumber || "-",
      width: "200px"
    },
    {
      name: "Created At",
      sortable: true,
      selector: (row) => capitalize(fDate(row.createdAt)),
    },

    // {
    //   name: "Status",
    //   cell: (row) => (
    //     <FormGroup>
    //       <FormControlLabel
    //         control={
    //           <Switch
    //             checked={row.status === 1}
    //             onClick={() => {
    //               setShow(true);
    //               setCurrentStatus(row.status);
    //               setStatusId(row._id);
    //               setWarning(
    //                 `Are you sure you want to ${row.status === 1 ? "deactivate" : "activate"
    //                 } this user?`
    //               );
    //             }}
    //             inputProps={{ "aria-label": "controlled" }}
    //           />
    //         }
    //       />
    //     </FormGroup>
    //   ),
    // },

    {
      name: "Action",
      right: true,
      cell: (row) => (
        <>
          <Tooltip title="View" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/wallet-management/transaction-history/${row?._id}?page=${currentPage}&view=${viewMode}`);
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          {user?.role !== ADMIN_ROLES.JUNIOR && (
            <Tooltip title="Edit Wallet" placement="top">
              <IconButton
                color="secondary"
                onClick={() => {
                  navigate(`/wallet-management/edit/${row?._id}?page=${currentPage}&view=${viewMode}`);
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  const renderWalletCard = (wallet, index) => (
    <Zoom in={!isLoading} className={`wallet-card-zoom wallet-card-zoom-${index}`}>
      <Grid item xs={12} sm={6} md={4} lg={3} key={wallet._id}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.05) 100%)',
            border: '1px solid',
            borderColor: 'divider',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            '&:hover': {
              borderColor: 'primary.main',
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 40px rgba(0, 161, 221, 0.2)',
            }
          }}
        >
          {/* Header */}
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2, gap: 1 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                boxShadow: '0 4px 16px rgba(0, 161, 221, 0.3)'
              }}
            >
              <AccountBalanceWalletIcon sx={{ fontSize: 24, color: 'white' }} />
            </Avatar>
            <div style={{display:"flex", gap:"5px", flexWrap:"wrap", justifyContent:"flex-end"}}>
             <Chip
              label={`£${formatPrice(wallet.balance || 0)}`}
              size="small"
              sx={{
                backgroundColor: 'rgba(76, 175, 80, 0.2)',
                color: '#4caf50',
                fontWeight: 600,
                border: '1px solid',
                borderColor: 'rgba(76, 175, 80, 0.3)'
              }}
            />
            <Chip
              icon={<Diamond color="blue" />}
              label={`${formatPrice(wallet.gemBalance || 0)}`}
              size="small"
              sx={{
                backgroundColor: "rgba(76, 97, 175, 0.2)",
                color: "#4c5bafff",
                fontWeight: 600,
                border: "1px solid",
                borderColor: "rgba(81, 76, 175, 0.3)"
              }}
            /> 
            <Chip
              icon={<Token style={{ fontSize: '16px', color: '#ffc107' }} />}
              label={`${formatPrice(wallet.sdCoinBalance || 0)}`}
              size="small"
              sx={{
                backgroundColor: "rgba(255, 193, 7, 0.2)",
                color: "#ffc107",
                fontWeight: 600,
                border: "1px solid",
                borderColor: "rgba(255, 193, 7, 0.3)",
                '& .MuiChip-icon': {
                  color: '#ffc107'
                }
              }}
            />
            </div>
           
          </Stack>

          {/* Content */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
              {capitalize(TrimText(wallet.firstname + " " + wallet.lastname, 30)) || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              @{wallet.username || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              {wallet.email || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              {wallet.phoneNumber || 'N/A'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
              Created: {fDate(wallet.createdAt)}
            </Typography>
          </Box>

          {/* Actions */}
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              startIcon={<VisibilityIcon />}
              onClick={() => navigate(`/wallet-management/transaction-history/${wallet._id}?page=${currentPage}&view=${viewMode}`)}
              sx={{
                borderRadius: 2,
                background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0091c8 0%, #3b8fd9 100%)',
                }
              }}
            >
              View History
            </Button>
            {user?.role !== ADMIN_ROLES.JUNIOR && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/wallet-management/edit/${wallet._id}?page=${currentPage}&view=${viewMode}`)}
                sx={{
                  borderRadius: 2,
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                Edit Wallet
              </Button>
            )}
          </Stack>
        </Paper>
      </Grid>
    </Zoom>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Fade in timeout={500}>
        <Stack spacing={4}>
          {/* Header Section */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
              border: '1px solid',
              borderColor: 'divider',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #00a1dd 0%, #42a5f5 50%, #90caf9 100%)',
              }
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={3}>
                <Avatar
        sx={{
                    width: 64,
                    height: 64,
                    background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                    boxShadow: '0 8px 32px rgba(0, 161, 221, 0.4)'
                  }}
                >
                  <AccountBalanceWalletIcon sx={{ fontSize: 32, color: 'white' }} />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                    Wallet Management
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Manage and monitor all user wallets and balances
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Paper>

          {/* Controls Section */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap', gap: 2 }}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
        <Input
          value={searchItem}
                  placeholder="Search wallets..."
          onChange={handleSearchChange}
                  sx={{
                    color: 'white',
                    '& .MuiInput-input': {
                      color: 'white',
                    },
                    '& .MuiInput-input::placeholder': {
                      color: 'rgba(255, 255, 255, 0.5)',
                      opacity: 1,
                    },
                    '& .MuiInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 2,
                      padding: '8px 16px',
                    }
                  }}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="clear-search"
                onClick={() => {
                  setSearchItem("");
                  fetchUsers("");
                }}
                        sx={{ color: 'text.secondary' }}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        />
              </Stack>

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode) {
                    setViewMode(newMode);
                    // Update URL parameter to maintain view mode
                    const newSearchParams = new URLSearchParams(searchParams);
                    newSearchParams.set('view', newMode);
                    setSearchParams(newSearchParams);
                  }
                }}
                sx={{
                  '& .MuiToggleButton-root': {
                    color: 'text.secondary',
                    borderColor: 'divider',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      }
                    }
                  }
                }}
              >
                <ToggleButton value="grid">
                  <ViewModuleIcon />
                </ToggleButton>
                <ToggleButton value="list">
                  <ViewListIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Paper>

          {/* Content Section */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            }}
          >
            {viewMode === 'grid' ? (
              /* Grid View */
              <Box sx={{ p: 3 }}>
                {isLoading || !loaderDelay ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <Loader />
                  </Box>
                ) : !data || data.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 3,
                        background: 'linear-gradient(135deg, #9c27b0 0%, #e91e63 100%)',
                        boxShadow: '0 8px 32px rgba(156, 39, 176, 0.3)'
                      }}
                    >
                      <AccountBalanceWalletIcon sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                      No wallets found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting your search criteria
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Grid container spacing={3}>
                      {data.map((wallet, index) => renderWalletCard(wallet, index))}
                    </Grid>
                    
                    {/* Pagination Controls (Grid View) */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      gap: 2,
                      flexWrap: 'wrap',
                      mt: 4,
                      p: 2,
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}>
                      {/* Center - Item Range Display */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          {totalPages > 0 ? (
                            `Showing ${((currentPage || 1) - 1) * pageSize + 1} to ${Math.min((currentPage || 1) * pageSize, totalPages)} of ${totalPages} wallets`
                          ) : (
                            'No wallets found'
                          )}
                        </Typography>
                      </Box>
                      {/* Left side - Page Size Selector */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: 'white', whiteSpace: 'nowrap' }}>
                          Items per page:
                        </Typography>
                        <FormControl size="small">
                          <Select
                            value={pageSize}
                            onChange={(e) => {
                              setPageSize(e.target.value);
                              setCurrentPage(1);
                              const newSearchParams = new URLSearchParams(searchParams);
                              newSearchParams.set('pageSize', e.target.value);
                              newSearchParams.set('page', '1');
                              setSearchParams(newSearchParams);
                            }}
                            displayEmpty
                            sx={{
                              color: 'white',
                              minWidth: 80,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255, 255, 255, 0.5)',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255, 255, 255, 0.7)',
                              },
                              '& .MuiSvgIcon-root': {
                                color: 'white',
                              },
                            }}
                          >
                            <MenuItem value={2}>2</MenuItem>
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      {/* Right side - Navigation Controls */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={totalPages === 0 || (currentPage || 1) === 1}
                          onClick={() => handlePageChange((currentPage || 1) - 1)}
                          sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                        >
                          Previous
                        </Button>
                        <Typography variant="body2" sx={{ color: 'white', mx: 1 }}>
                          {totalPages > 0 ? (
                            `Page ${currentPage || 1} of ${Math.ceil(totalPages / pageSize)}`
                          ) : (
                            'No data'
                          )}
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={totalPages === 0 || (currentPage || 1) >= Math.ceil(totalPages / pageSize)}
                          onClick={() => handlePageChange((currentPage || 1) + 1)}
                          sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                        >
                          Next
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
      </Box>
            ) : (
              /* List View */
              <Box>
        <DataTable
          customStyles={{
            headCells: {
              style: {
                fontSize: "15px",
                fontWeight: "bold",
                        backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        color: 'black'
                      },
                    },
                    rows: {
                      style: {
                        backgroundColor: 'transparent',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        }
                      }
                    },
                    cells: {
                      style: {
                        color: 'white',
                      }
                    }
          }}
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalPages || 0}
          paginationCurrentPage={currentPage || 1}
          paginationDefaultPage={currentPage || 1}
          onChangePage={handlePageChange}
          paginationPerPage={pageSize}
          paginationRowsPerPageOptions={[10, 20, 50]}
          onChangeRowsPerPage={(currentRowsPerPage) =>
            setPageSize(currentRowsPerPage)
          }
          paginationComponentOptions={{
            rowsPerPageText: 'Rows per page:',
            rangeSeparatorText: 'of',
            selectAllRowsItem: true,
            selectAllRowsItemText: 'All',
          }}
          progressPending={isLoading || !loaderDelay}
          progressComponent={<Loader />}
          className="custom"
        />
              </Box>
            )}
          </Paper>
        </Stack>
      </Fade>
    </Container>
  );
}
